import { format, isSameDay } from 'date-fns'
import React from 'react'
import { Spacing, Theme } from '@opallabs/gem'
import { AnalyticsLayer, AnalyticsConsumer } from '@opallabs/quanta'
import CardContext from '../../cardContext'
import { CreateCard } from '../../CreateCard'
import { isWithinStoryDateRange } from '../../helpers'
import MomentCard from '../../MomentCard'
import { DefaultMomentCard } from '../../MomentCard/Default'
import { WeekViewCategory } from '../../types'
import { useMomentMap } from '../../useMomentMap'
import { makeMapKey } from '../../useMomentMap/momentMap'
import { WeekViewGlobalContext } from '../../weekViewGlobalContext'
import * as styles from './styles'

type MomentStackProps = Readonly<{
  activeFilters: string[]
  selectedFilters?: Record<string, unknown>
  /** Ensures only the designated stack will display "no moments" when appropriate */
  allowNoMomentsPlaceholder: boolean
  category: WeekViewCategory
  date: Date
  theme?: Theme
}>

export const MomentStack: React.FC<MomentStackProps> = ({
  activeFilters,
  selectedFilters,
  allowNoMomentsPlaceholder,
  category,
  date,
  theme = 'light',
}) => {
  const { momentMap, categoryCounts, getMoment } = useMomentMap()

  const key = makeMapKey(date, category.id)
  const momentIds = momentMap.get(key)

  const displayNoMomentsPlacehodler = React.useMemo(() => {
    const count = categoryCounts.get(category.id)
    return (
      allowNoMomentsPlaceholder && (typeof count !== 'number' || count === 0)
    )
  }, [allowNoMomentsPlaceholder, category.id, categoryCounts])

  const {
    activeColumn,
    contextMenuCallbacks,
    disableActiveColumn,
    isLimitedUser,
    isLoadingMoments,
    isMomentCreatorActive,
    onAddMomentCreator,
    placeholderMoment,
    setDisableActiveColumn,
    setPlaceholderMoment,
    story,
  } = React.useContext(WeekViewGlobalContext)

  const { createMoment } = React.useContext(CardContext) ?? {}

  let message = ''
  let disableButton = false

  if (!isWithinStoryDateRange(date, story)) {
    message = 'Outside of story date range'
    disableButton = true
  }

  if (isLimitedUser) {
    message = 'Current user not permitted to create moment'
    disableButton = true
  }

  const isActiveColumn =
    isSameDay(date, activeColumn.date) &&
    activeColumn.category === category.title &&
    !disableActiveColumn

  const showPlaceholderMoment =
    isMomentCreatorActive &&
    placeholderMoment &&
    isSameDay(new Date(placeholderMoment.scheduled_at), date) &&
    placeholderMoment.day_segment === category.id

  const onClickAdd = () => {
    setDisableActiveColumn(true)
    onAddMomentCreator(category, date)
  }

  const onRemoveCreator = () => {
    setPlaceholderMoment(null)
    setDisableActiveColumn(false)
  }

  const onCreateMoment = (
    title: string,
    selectedServices?: Array<{ id: string }>
  ) => {
    // Right before we save, send scheduled_at as a date-only string
    // to ensure moments get scheduled for the user-selected date,
    // but in the server's timezone. This avoids date shifting, since
    // most timezones are after (but within 24 hours of) the server's
    // timezone (currently PST).

    createMoment?.(
      {
        title,
        day_segment: category.id,
        scheduled_at: format(date, 'yyyy-MM-dd'),
      },
      selectedServices
    )
  }

  return (
    <React.Fragment>
      {momentIds?.map((id) => (
        <div css={{ marginBlockEnd: Spacing.M }} key={`${key}-${id}`}>
          <AnalyticsLayer name="Moment Card">
            <AnalyticsConsumer>
              {({ track }) => (
                <MomentCard
                  activeFilters={activeFilters}
                  selectedFilters={selectedFilters}
                  moment={getMoment(id)}
                  {...{
                    contextMenuCallbacks,
                    isLimitedUser,
                    isLoadingMoments,
                    story,
                    theme,
                    track,
                  }}
                />
              )}
            </AnalyticsConsumer>
          </AnalyticsLayer>
        </div>
      ))}

      {displayNoMomentsPlacehodler &&
        !isActiveColumn &&
        !showPlaceholderMoment && (
          /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
          <div
            role="status"
            aria-live="polite"
            aria-label={`${category.title} contains zero moments`}
            css={styles.emptyCategoryContainer}
            tabIndex={0}
          >
            <span css={styles.emptyCategoryPill}>No scheduled moments</span>
          </div>
          /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
        )}

      {showPlaceholderMoment ? (
        <div css={{ position: 'relative' }}>
          <DefaultMomentCard
            activeFilters={activeFilters}
            onCreateMoment={onCreateMoment}
            onRemoveCreator={onRemoveCreator}
          />
        </div>
      ) : null}

      {!showPlaceholderMoment && (
        <div css={styles.addCardWrapper}>
          <CreateCard
            disableButton={disableButton}
            tooltipMessage={message}
            isVisible={isActiveColumn}
            onClickAdd={onClickAdd}
          />
        </div>
      )}
    </React.Fragment>
  )
}
